import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { adminApi } from "../lib/adminApi";

const NAV = [
  { to: "/admin/dashboard", label: "Enquiries", testid: "admin-nav-enquiries" },
  { to: "/admin/jobs", label: "Job Openings", testid: "admin-nav-jobs" },
  { to: "/admin/applications", label: "Applications", testid: "admin-nav-applications" },
  { to: "/admin/settings", label: "Settings", testid: "admin-nav-settings" },
];

const AdminLayout = ({ title, children }) => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    adminApi.get("/auth/me").then((res) => setAdmin(res.data)).catch(() => navigate("/admin"));
  }, [navigate]);

  const logout = async () => {
    await adminApi.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  if (!admin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-surface">
        <p className="text-sm text-brand-grey">Checking session…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-brand-surface" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-border bg-white" data-testid="admin-header">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-4">
            <img
              src="/assets/vaskion-logo.png"
              alt="Vaskion Technologies Private Limited"
              className="h-10 w-auto shrink-0 sm:h-12"
              data-testid="admin-header-logo"
            />
            <div className="hidden h-8 w-px bg-border sm:block" aria-hidden="true" />
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold text-brand-graphite sm:text-lg" data-testid="admin-header-title">
                {title}
              </h1>
              <p className="hidden truncate text-xs text-brand-grey sm:block">Signed in as {admin.email}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-brand-grey transition-colors hover:text-brand-red"
              data-testid="admin-view-website-link"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">View Website</span>
            </a>
            <button type="button" onClick={logout} className="btn-secondary flex items-center gap-2 text-sm" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" aria-hidden="true" /> Sign Out
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-7xl gap-1 px-4 sm:px-6" aria-label="Admin sections" data-testid="admin-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-brand-red text-brand-graphite"
                    : "border-transparent text-brand-grey hover:text-brand-graphite"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6">{children}</main>

      <footer className="border-t border-border bg-brand-graphite" data-testid="admin-footer">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6">
          <p className="text-center text-sm text-neutral-400" data-testid="admin-footer-copyright">
            © 2026 Vaskion Technologies Private Limited. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AdminLayout;
